import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { FileText, Clock, CheckCircle, Upload, Eye } from 'lucide-react'
import { toast } from 'react-toastify'

interface DigitalAgreement {
  id: string
  type: string
  title: string
  description: string
  fileUrl: string
  status: string
  signedAt: string | null
  buyerSignedAt: string | null
  vendorSignedAt: string | null
  buyerSignedBy: string | null
  vendorSignedBy: string | null
  createdAt: string
  po: {
    poNumber: string
    buyer: {
      email: string
      firstName: string
      lastName: string
      companyName: string
    }
    vendor: {
      email: string
      firstName: string
      lastName: string
      companyName: string
    }
  }
  uploader: {
    email: string
    firstName: string
    lastName: string
  }
  buyerSigner?: {
    email: string
    firstName: string
    lastName: string
  }
  vendorSigner?: {
    email: string
    firstName: string
    lastName: string
  }
}

const DigitalAgreementsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [agreements, setAgreements] = useState<DigitalAgreement[]>([])
  const [stats, setStats] = useState<any>({ total: 0, pending: 0, signed: 0 })
  const [loading, setLoading] = useState(true)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedAgreement, setSelectedAgreement] = useState<DigitalAgreement | null>(null)
  const [uploadFileUrl, setUploadFileUrl] = useState('')

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }
    loadAgreements()
    loadStats()
  }, [user, navigate])

  const loadAgreements = async () => {
    try {
      setLoading(true)
      const response = await apiService.get('/api/post-quote-activity/digital-agreements') as any
      setAgreements(response.data)
    } catch (error) {
      console.error('Error loading agreements:', error)
      toast.error('Failed to load agreements')
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await apiService.get('/api/post-quote-activity/digital-agreements/statistics') as any
      setStats(response.data || { total: 0, pending: 0, signed: 0 })
    } catch (error) {
      console.error('Error loading stats:', error)
      setStats({ total: 0, pending: 0, signed: 0 })
    }
  }

  // Helper functions for dual signature logic
  const isCurrentUserBuyer = (agreement: DigitalAgreement) => {
    return user?.email === agreement.po.buyer.email
  }

  const isCurrentUserVendor = (agreement: DigitalAgreement) => {
    return user?.email === agreement.po.vendor.email
  }

  const canCurrentUserSign = (agreement: DigitalAgreement) => {
    if (agreement.status === 'FULLY_SIGNED') return false
    
    const isBuyer = isCurrentUserBuyer(agreement)
    const isVendor = isCurrentUserVendor(agreement)
    
    if (agreement.status === 'PENDING') {
      // In PENDING state, either party can sign first
      return isBuyer || isVendor
    } else if (agreement.status === 'VENDOR_SIGNED') {
      // Only buyer can sign after vendor
      return isBuyer
    } else if (agreement.status === 'BUYER_SIGNED') {
      // Only vendor can sign after buyer
      return isVendor
    }
    
    return false
  }

  const getSigningStatusText = (agreement: DigitalAgreement) => {
    if (agreement.status === 'FULLY_SIGNED') {
      return '✅ Fully signed by both parties'
    } else if (agreement.status === 'PENDING') {
      return '⏳ Waiting for first signature'
    } else if (agreement.status === 'VENDOR_SIGNED') {
      return '✅ Vendor signed - Waiting for buyer'
    } else if (agreement.status === 'BUYER_SIGNED') {
      return '✅ Buyer signed - Waiting for vendor'
    }
    return 'Unknown status'
  }

  const handleSignAgreement = async (agreementId: string) => {
    try {
      await apiService.patch(`/api/post-quote-activity/digital-agreements/${agreementId}/sign`)
      toast.success('Agreement signed successfully')
      loadAgreements()
      loadStats()
    } catch (error) {
      console.error('Error signing agreement:', error)
      toast.error('Failed to sign agreement')
    }
  }

  const handleUploadFile = async (agreementId: string) => {
    if (!uploadFileUrl.trim()) {
      toast.error('Please enter a file URL')
      return
    }

    try {
      await apiService.patch(`/api/post-quote-activity/digital-agreements/${agreementId}/upload`, {
        fileUrl: uploadFileUrl
      })
      toast.success('File uploaded successfully')
      setShowUploadModal(false)
      setUploadFileUrl('')
      loadAgreements()
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'FULLY_SIGNED':
        return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'BUYER_SIGNED':
      case 'VENDOR_SIGNED':
        return <Clock className="h-5 w-5 text-blue-600" />
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-600" />
      default:
        return <Clock className="h-5 w-5 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FULLY_SIGNED':
        return 'bg-green-100 text-green-800'
      case 'BUYER_SIGNED':
      case 'VENDOR_SIGNED':
        return 'bg-blue-100 text-blue-800'
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow">
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Digital Agreements</h1>
            <p className="text-gray-600 mt-2">Manage and sign your business agreements</p>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Agreements</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.pending || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Signed</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.signed || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Agreements List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Your Agreements</h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {agreements.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No agreements found</h3>
                <p className="text-gray-600">Digital agreements will appear here once created for your purchase orders.</p>
              </div>
            ) : (
              agreements.map((agreement) => (
                <div key={agreement.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(agreement.status)}
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{agreement.title}</h3>
                          <p className="text-sm text-gray-600">{agreement.description}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">PO Number:</span>
                          <span className="ml-2 text-gray-600">{agreement.po.poNumber}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Type:</span>
                          <span className="ml-2 text-gray-600">{agreement.type}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Status:</span>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agreement.status)}`}>
                            {agreement.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Buyer:</span>
                          <span className="ml-2 text-gray-600">{agreement.po.buyer.companyName || `${agreement.po.buyer.firstName} ${agreement.po.buyer.lastName}`}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Vendor:</span>
                          <span className="ml-2 text-gray-600">{agreement.po.vendor.companyName || `${agreement.po.vendor.firstName} ${agreement.po.vendor.lastName}`}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2 ml-6">
                      {/* Show signing status */}
                      <div className="text-xs text-gray-600 mb-2">
                        {getSigningStatusText(agreement)}
                      </div>
                      
                      {/* Show who has signed */}
                      {(agreement.buyerSignedAt || agreement.vendorSignedAt) && (
                        <div className="text-xs text-gray-500 mb-2">
                          {agreement.buyerSignedAt && (
                            <div>✅ Buyer signed: {new Date(agreement.buyerSignedAt).toLocaleDateString()}</div>
                          )}
                          {agreement.vendorSignedAt && (
                            <div>✅ Vendor signed: {new Date(agreement.vendorSignedAt).toLocaleDateString()}</div>
                          )}
                        </div>
                      )}
                      
                      {/* Action buttons */}
                      <div className="flex flex-col space-y-1">
                        {!agreement.fileUrl && (
                          <button
                            onClick={() => {
                              setSelectedAgreement(agreement)
                              setShowUploadModal(true)
                            }}
                            className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                          >
                            <Upload className="h-3 w-3 mr-1" />
                            Upload File
                          </button>
                        )}
                        
                        {agreement.fileUrl && canCurrentUserSign(agreement) && (
                          <button
                            onClick={() => handleSignAgreement(agreement.id)}
                            className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Sign Agreement
                          </button>
                        )}
                        
                        {agreement.fileUrl && !canCurrentUserSign(agreement) && agreement.status !== 'FULLY_SIGNED' && (
                          <div className="text-xs text-gray-500 px-3 py-1 bg-gray-100 rounded">
                            {isCurrentUserBuyer(agreement) && agreement.status === 'BUYER_SIGNED' && 'You have already signed'}
                            {isCurrentUserVendor(agreement) && agreement.status === 'VENDOR_SIGNED' && 'You have already signed'}
                            {agreement.status === 'VENDOR_SIGNED' && isCurrentUserBuyer(agreement) && 'Waiting for you to sign'}
                            {agreement.status === 'BUYER_SIGNED' && isCurrentUserVendor(agreement) && 'Waiting for you to sign'}
                          </div>
                        )}
                        
                        {agreement.fileUrl && (
                          <a
                            href={agreement.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View File
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && selectedAgreement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <Upload className="h-8 w-8 text-blue-600" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Upload Agreement File
                  </h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Please provide a URL to the agreement document (PDF, DOC, etc.)
                </p>
                <input
                  type="url"
                  value={uploadFileUrl}
                  onChange={(e) => setUploadFileUrl(e.target.value)}
                  placeholder="https://example.com/agreement.pdf"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowUploadModal(false)
                    setUploadFileUrl('')
                    setSelectedAgreement(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUploadFile(selectedAgreement.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DigitalAgreementsPage 